import { Avatar } from '../../../layout/Avatar.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Card } from '../../../layout/Card.jsx';
import { formatDateTime } from '../../../shared/format.js';
import { useDuration } from '../../../shared/useDuration.js';

function pricingLabel(t) {
  if (t === 'package') return 'باقة';
  if (t === 'open') return 'وقت مفتوح';
  if (t === 'fixed') return 'مبلغ ثابت';
  return 'ساعي';
}

function genderLabel(g) {
  if (g === 'male') return 'ذكر';
  if (g === 'female') return 'أنثى';
  return g || '';
}

function resolvePhoto(url) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('/api/') || url.startsWith('/uploads/')) return url;
  return `/api/children/photos/${url.replace(/^\//, '')}`;
}

export function ChildSummary({ session }) {
  // Backend populates `childId` / `guardianId` with full objects.
  const child = session?.childId || session?.child || {};
  const guardian = session?.guardianId || session?.guardian || {};
  const duration = useDuration(session?.startTime, 1000);

  const photoUrl = resolvePhoto(child.photoUrl);

  const isOverstay =
    session?.status === 'expired' ||
    session?.status === 'overstay' ||
    (session?.expectedEndTime && new Date(session.expectedEndTime).getTime() < Date.now());
  const hasPending = (session?.pendingAmount || 0) > 0 || session?.hasPendingInvoices;

  return (
    <Card>
      <div className="flex flex-col items-center gap-2">
        <Avatar src={photoUrl} name={child.name || '?'} id={child._id || child.id} size="xl" />
        <div className="text-center">
          <div className="font-extrabold text-[18px]">{child.name || '—'}</div>
          <div className="text-[12px] text-ink-3 mt-0.5">
            {child.age ? `${child.age} سنوات` : ''}
            {child.gender ? ` • ${genderLabel(child.gender)}` : ''}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-center">
          <Pill variant="orange">داخل القاعة</Pill>
          {hasPending ? <Pill variant="danger">عليه حساب</Pill> : null}
          {isOverstay ? <Pill variant="danger">تجاوز الوقت</Pill> : null}
        </div>
      </div>

      <div className="border-t border-line my-3" />

      <div className="space-y-2 text-[12.5px]">
        <Row label="ولي الأمر" value={guardian.name || '—'} />
        <Row label="الهاتف" value={guardian.phone || '—'} dir="ltr" />
        <Row
          label="الكارت"
          value={session?.cardUid || session?.cardId?.uid || session?.card?.uid || '—'}
          dir="ltr"
          mono
        />
        <Row
          label="وقت الدخول"
          value={formatDateTime(session?.startTime) || '—'}
        />
        {session?.expectedEndTime ? (
          <Row
            label="نهاية الباقة"
            value={formatDateTime(session.expectedEndTime)}
            valueColor={isOverstay ? '#B91C1C' : undefined}
          />
        ) : null}
        <Row
          label="المدة الحالية"
          value={
            <span className="font-mono font-bold" style={{ color: isOverstay ? '#B91C1C' : '#1A1A1A' }}>
              <Icon name="clock" size={12} className="inline -mt-0.5 ml-1" />
              {duration.formatted}
            </span>
          }
        />
        <Row label="نوع التسعير" value={pricingLabel(session?.pricingType)} />
      </div>
    </Card>
  );
}

function Row({ label, value, dir, mono, valueColor }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-ink-3">{label}</span>
      <span
        className={mono ? 'font-mono font-semibold' : 'font-semibold'}
        dir={dir}
        style={{ color: valueColor }}
      >
        {value}
      </span>
    </div>
  );
}
